import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import moment from "moment";

import CAStore from "../../Store/TemplateStore/template.store";
import { updatePlantInformation } from "../../network/cipAPI";
import { ValueStrings } from "../../utils/constantsString";

import CriticalDeleteDialog from "../../components/CriticalDeleteDialog";
import InputBox from "../../components/InputBox";
import DupontDatePicker from "../../components/DatePopover/DupontDatePicker";
import DupontRadio from "../../components/DupontRadio";
import { DatePickerConfig } from "../../components/DatePopover/DateTimeConfig";

import { PLANT_INFO, SELECTED_SECTION } from "./plantInformation.config";

export const store = CAStore;

const PlantInformation = React.forwardRef((props, ref) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const plantInfo = PLANT_INFO(t);
  const selectedSection = SELECTED_SECTION(t);

  const {
    plantName,
    plantLocation,
    water_type,
    num_trains,
    tol_num_elements,
    sel_train,
    sel_pass,
    sel_stage,
    sel_pressure_vessels,
    sel_element_involved,
    dataset_name,
    sel_element_type,
    is_dupont_element,
    selTrain: selectedTrain,
    // Threshold values
    max_feed_pressure,
    max_permeate_quality,
    std_warranty_period,
    startup_pressure_drop,
    designed_permeate_flow,
    std_membrane_age,
    max_pressure_drop,
    min_permeate_flow,
    // Cost and CIP values
    alkaliPrice,
    acidPrice,
    energyPrice,
    waterPrice,
    costofDownTime,
    additional_chemical_price,
    acid_used_per_cip,
    alkali_used_per_cip,
    additional_chemical_per_cip,
    acid_cip_ph,
    alkali_cip_ph,
    cip_solution_ph,
    acid_cip_temp,
    alkali_cip_temp,
    cip_solution_temp,
    acid_cip_duration,
    alkali_cip_duration,
    cip_solution_duration,
    // Installation date and warranty
    installation_date: storeInstallationDate,
    warranty: storeWarranty,
  } = useSelector((state) => state);

  const plantInfoValues = {
    plantName: plantName,
    plantLocation: plantLocation,
    water_type: water_type,
    num_trains: num_trains,
    tol_num_elements: tol_num_elements,
    sel_train: sel_train,
    sel_pass: sel_pass,
    sel_stage: sel_stage,
    sel_pressure_vessels: sel_pressure_vessels,
    sel_element_involved: sel_element_involved,
    dataset_name: dataset_name,
    sel_element_type: sel_element_type,
    is_dupont_element: is_dupont_element,
  };

  const replaceEmptyWithNull = (attribute) => {
    return attribute !== "" ? attribute : null;
  };

  const plantInformation = async () => {
    let additionErrors = false;

    if (
      (props.ra && installation_date === null) ||
      installation_date === "" ||
      installation_date === undefined
    ) {
      additionErrors = true;
      setInstallationDateError(
        t("plantInformationStrings.installationDateError")
      );
    }

    if (
      plantInfoValues.plantName &&
      plantInfoValues.plantLocation &&
      props.mandatoryThresholdFilled
    ) {
      if (props.ra && additionErrors) {
        return;
      } else {
        props.setActiveStep(1);
        let payload = {
          // setting: {
          //   flux: store.getState().flux,
          //   temperature: store.getState().temperature,
          //   permeate_quality: store.getState().permeate_quality,
          //   feed_quality: store.getState().feed_quality,
          //   pressure: store.getState().pressure,
          //   flow: store.getState().flow,
          //   volume: store.getState().volume,
          //   currency: store.getState().currency,
          //   conversion_rate: store.getState().conversion_rate,
          // },
        };
        if (props.ra) {
          const currentValues = {
            max_feed_pressure,
            max_permeate_quality,
            std_warranty_period,
            startup_pressure_drop,
            designed_permeate_flow,
            std_membrane_age,
            max_pressure_drop,
            min_permeate_flow,
          };

          // Only include values that have changed from their original values
          const changedThreshold = Object.entries(currentValues)
            .filter(([key, value]) => {
              const originalValue = originalThresholdValues[key];
              return value !== originalValue && value !== "";
            })
            .reduce(
              (acc, [key, value]) => ({
                ...acc,
                [key]: replaceEmptyWithNull(value),
              }),
              {}
            );

          const formattedInstallationDate = installation_date
            ? moment(installation_date).format(
                ValueStrings.installationDateFormat
              )
            : "";

          // Only include installation_date and warranty if they have changed
          const hasInstallationDateChanged =
            formattedInstallationDate !== originalInstallationDate;
          const hasWarrantyChanged = warranty !== originalWarranty;

          payload = {
            ...payload,
            ...(Object.keys(changedThreshold).length > 0 && {
              threshold: changedThreshold,
            }),
            ...(hasInstallationDateChanged && {
              installation_date: formattedInstallationDate,
            }),
            ...(hasWarrantyChanged && { warranty }),
          };
        } else {
          payload = {
            ...payload,
            cost: {
              alkali_price: replaceEmptyWithNull(alkaliPrice),
              acid_price: replaceEmptyWithNull(acidPrice),
              energy_price: replaceEmptyWithNull(energyPrice),
              water_price: replaceEmptyWithNull(waterPrice),
              downtime_cost: costofDownTime,
              additional_chemical_price,
            },
            default_cip: {
              acid_used_per_cip: replaceEmptyWithNull(acid_used_per_cip),
              alkali_used_per_cip: replaceEmptyWithNull(alkali_used_per_cip),
              additional_chemical_per_cip,
              acid_cip_ph,
              alkali_cip_ph,
              cip_solution_ph,
              acid_cip_temp,
              alkali_cip_temp,
              cip_solution_temp,
              acid_cip_duration,
              alkali_cip_duration,
              cip_solution_duration,
            },
          };
        }
        dispatch({
          type: "STORE_PLANT_DETAILS",
          payload: {
            installation_date: moment(installation_date).format(
              ValueStrings.installationDateFormat
            ),
            warranty: warranty,
          },
        });

        const response = await updatePlantInformation(payload, selectedTrain);
        setInstallationDateError(null);
      }
    }
  };

  React.useImperativeHandle(ref, () => ({ plantInformation }));
  const [installation_date, setInstallationDate] = useState(
    storeInstallationDate && storeInstallationDate !== "None"
      ? new Date(storeInstallationDate)
      : ""
  );
  const [installationDateError, setInstallationDateError] = useState("");
  const [warranty, setWarranty] = useState(storeWarranty.toString());
  const [warrantyPopup, setWarrantyPopup] = useState(false);
  const [originalThresholdValues] = useState({
    max_feed_pressure,
    max_permeate_quality,
    std_warranty_period,
    startup_pressure_drop,
    designed_permeate_flow,
    std_membrane_age,
    max_pressure_drop,
    min_permeate_flow,
  });

  const [originalInstallationDate] = useState(
    storeInstallationDate && storeInstallationDate !== "None"
      ? storeInstallationDate
      : ""
  );
  const [originalWarranty] = useState(storeWarranty?.toString() || "false");

  const onDateChange = (value) => {
    setInstallationDateError("");
    setInstallationDate(value);
  };
  const handleOptionChange = (event) => {
    const { name, value } = event.target;
    setWarranty(value);
    if (value === "true") setWarrantyPopup(true);
  };
  const onCloseClicked = (event) => {
    setWarrantyPopup(false);
  };

  return (
    <div
      className="lightGrayBorder"
      style={{ padding: "16px", marginTop: "16px" }}
    >
      <div className="headerTealText">
        {t("plantInformationStrings.plantInfo")}
      </div>
      <div className="plant-grid-container">
        {Object.keys(plantInfo).map((info) => (
          <div className="plant-grid-item">
            <InputBox
              value={plantInfoValues[info]}
              placeholder={plantInfo[info].labelText}
              labelText={plantInfo[info].labelText}
              disabled
              hideClearBtn
            />
          </div>
        ))}

        {props.ra && (
          <>
            <div className="grid-item">
              <DupontDatePicker
                dateFormat={t("ValueStrings.DATE_FORMAT_SMALL")}
                selected={installation_date ? installation_date : ""}
                onChange={onDateChange}
                startDate={new Date()}
                label={t("plantInformationStrings.installation_date")}
                //minDate={cipStartDate}
                maxDate={new Date()}
                labelClass="dupont-form-label wrapText text-left"
                className={"width-fill"}
                isRequired
                error={installationDateError}
                {...DatePickerConfig}
              />
            </div>
            <div className="grid-item">
              <div className="checkbox-label-width ">
                {t("plantInformationStrings.warranty")}
              </div>
              <div
                key="warrantyGroup"
                onChange={handleOptionChange}
                className="checkbox-container"
              >
                <DupontRadio
                  text={t("ValueStrings.yes")}
                  checked={warranty === "true"}
                  value={"true"}
                  name="warrantyGroup"
                />
                <DupontRadio
                  text={t("ValueStrings.no")}
                  checked={warranty === "false"}
                  value={"false"}
                  name="warrantyGroup"
                />
              </div>
            </div>
            {warrantyPopup && (
              <CriticalDeleteDialog
                type="warning"
                title={t("plantInformationStrings.warrantyNotice")}
                description={t("plantInformationStrings.warrantyWarning")}
                onDialogClose={onCloseClicked}
                cancelBtnText={t("ValueStrings.close")}
              />
            )}
          </>
        )}
      </div>
      <div className="margin-top-20 font-bold ">
        {t("plantInformationStrings.selectedSection")}
        <div className="selected-grid-container ">
          {Object.keys(selectedSection).map((info) => (
            <div className="plant-grid-item">
              <InputBox
                value={plantInfoValues[info]}
                placeholder={selectedSection[info].labelText}
                labelText={selectedSection[info].labelText}
                hideClearBtn
                disabled
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
});

export default PlantInformation;
